<template>
  <div class="my-10 flex flex-col">
    <CommentModal :item-id="currentId" origin="exhibition"></CommentModal>
    <ReportModal :item-id="currentId" origin="exhibition"></ReportModal>
    <RateModal @rated="setUserRate" :item-id="currentId" origin="exhibition"></RateModal>
    <div class="grid grid-cols-2 gap-5 max-lg:grid-cols-1">
      <div class="flex flex-col">
        <div>
          <video
            controls
            controlsList="nodownload"
            v-if="isVideo(currentImage)"
            :src="currentImage"
            class="w-full h-[25rem] object-contain rounded-lg cursor-pointer"
          ></video>
          <template v-else>
            <img
              @click="onShow"
              :src="currentImage"
              alt="main-image"
              class="w-full h-[25rem] object-fill rounded-lg cursor-pointer"
            />
            <vue-easy-lightbox
              :visible="visibleRef"
              :imgs="currentImage"
              :index="indexRef"
              @hide="onHide"
            ></vue-easy-lightbox>
          </template>
        </div>
        <div>
          <Splide class="" :has-track="false" :options="imageOptions" aria-label="carouselBanner">
            <SplideTrack class="rounded-lg py-2">
              <SplideSlide class="" v-for="(item, index) in detail.images" :key="item.id">
                <video
                  controlsList="nodownload"
                  v-if="isVideo(item.path)"
                  @click="changeImage(index)"
                  :src="item.path"
                  class="h-32 w-full object-cover rounded-md cursor-pointer"
                ></video>
                <img
                  v-else
                  @click="changeImage(index)"
                  :src="item.path"
                  class="h-32 w-full object-fill rounded-md cursor-pointer"
                  alt=""
                />
              </SplideSlide>
            </SplideTrack>
          </Splide>
        </div>
        <div v-if="advertisements.length > 0">
          <DetailBanner :content="advertisements"></DetailBanner>
        </div>
        <router-link :to="{ name: 'foodRestaurantDetail', params: { id: detail?.store?.id } }">
          <div class="mt-5 bg-white p-5 rounded-md">
            <div>
              <p class="text-main-color font-bold text-xl">{{ $t('nav.restaurant') }}</p>
            </div>
            <div class="flex gap-5 mt-4 min-w-0">
              <div class="shrink-0">
                <img :src="detail?.store?.thumbnail" class="h-36 rounded-md object-cover" alt="" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="mt-1 text-lg font-bold truncate">
                  {{ detail?.store?.name }}
                </p>

                <p class="mt-1 flex gap-1 min-w-0">
                  <span class="shrink-0">{{ $t('page.location') }}:</span>
                  <span class="font-semibold truncate min-w-0">
                    {{ detail?.store?.location }}
                  </span>
                </p>
                <p v-if="restaurantFirstPhone" class="mt-1 flex gap-1 min-w-0">
                  <span class="shrink-0">{{ $t('page.phoneNumbers') }}:</span>
                  <span class="font-semibold truncate min-w-0">
                    {{ restaurantFirstPhone }}
                  </span>
                </p>

                <p v-if="detail?.store?.caption" class="mt-1 text-sm text-[#333] truncate">
                  {{ detail?.store?.caption }}
                </p>
              </div>
            </div>
          </div>
        </router-link>
      </div>
      <div>
        <div>
          <div class="border-b border-[#E3E3E3] mb-4">
            <div class="flex gap-3 mb-3">
              <span
                v-if="detail?.is_available && detail?.is_new"
                class="m-1 bg-green-500 text-white text-sm font-semibold rounded-lg p-1"
                >{{ $t('page.new') }}</span
              >
              <span
                v-if="detail?.is_available && detail?.in_promotion"
                class="m-1 bg-red-500 text-white text-sm w-7 h-7 text-center font-semibold rounded-full p-1"
                >A</span
              >
              <span
                v-if="detail?.is_available && detail?.discount_percent"
                class="m-1 font-semibold bg-red-500 text-white text-sm text-center rounded-lg p-1"
                >-{{ detail?.discount_percent }}%</span
              >
            </div>

            <div class="flex justify-between items-center min-w-0">
              <p class="font-semibold text-main-color text-3xl mb-3 truncate min-w-0 flex-1 pr-4">
                {{ detail.name }}
              </p>
              <button
                :disabled="loading"
                class="rounded-full bg-[#409DBA] h-[2.1875rem] w-[2.1875rem] flex justify-center items-center cursor-pointer shrink-0"
                @click="likeAction"
              >
                <IconHeart :class="{ 'fill-white': this.isLiked }"></IconHeart>
              </button>
            </div>

            <div class="flex items-center justify-between gap-5 mb-6">
              <div class="flex gap-5 items-center">
                <p v-if="detail?.discount == null" class="text-[#409DBA] font-bold text-2xl">
                  {{ formatNumber(detail?.price) }} TMT
                </p>
                <div class="">
                  <p class="text-[#409DBA] font-bold text-2xl" v-if="detail?.discount_percent">
                    {{
                      formatNumber(detail?.price - detail?.price * (detail?.discount_percent / 100))
                    }}
                    TMT
                    <span class="line-through font-semibold text-lg text-[#CB3131]"
                      >{{ formatNumber(detail?.price) }} TMT</span
                    >
                  </p>
                </div>
                <div class="flex items-center gap-1">
                  <IconEye></IconEye>
                  <p class="text-main-color font-semibold text-sm">{{ detail.view_count }}</p>
                </div>
              </div>
              <VueStarRating
                :rounded-corners="true"
                :star-size="15"
                active-color="#ffb300"
                inactive-color="#f2f2f2"
                :border-width="2"
                border-color="#ffb300"
                :show-rating="false"
                :read-only="true"
                :increment="0.5"
                :rating="detail?.rating"
              ></VueStarRating>
            </div>
            <div v-if="!detail?.is_available" class="mb-3">
              <p class="text-red-500 font-semibold text-lg">{{ $t('page.outOfStock') }}</p>
            </div>
            <div
              v-if="detail?.is_available && detail?.in_promotion"
              class="mb-3 flex font-semibold text-xl items-center gap-2 text-red-500"
            >
              {{ $t('page.promotion') }}
            </div>
            <div class="mb-3 flex items-center gap-2">
              <p>{{ $t('page.yourRate') }}:</p>
              <VueStarRating
                :rounded-corners="true"
                :star-size="15"
                active-color="#ffb300"
                inactive-color="#f2f2f2"
                :border-width="2"
                border-color="#ffb300"
                :show-rating="false"
                :increment="1"
                :read-only="true"
                :rating="userRating"
              ></VueStarRating>
            </div>
          </div>

          <div class="w-full">
            <div class="">
              <template v-if="detail?.store?.can_online_order">
                <div v-if="detail?.is_available" class="w-full mb-3">
                  <SubmitBtn
                    v-if="cartItem === null"
                    @onclick="addToCart"
                    :loading="loading"
                    class="!w-full block text-center rounded-sm bg-main-color text-base py-4 text-white font-semibold"
                  >
                    {{ $t('page.addToCart') }}</SubmitBtn
                  >
                  <div
                    v-else
                    class="flex items-center gap-5 mt-10 max-md:mt-6 max-md:justify-between"
                  >
                    <button
                      @click="updateCartItem(cartItem.quantity - 1)"
                      class="rounded-lg inline-flex justify-center items-center h-12.5 w-12.5 bg-main-color ease-linear transition-all duration-150 hover:bg-accent-color max-md:w-20 max-md:h-11"
                    >
                      <IconMinus
                        svgClass="h-6 w-6 max-md:h-5 max-md:w-5"
                        pathClass="stroke-white"
                      />
                    </button>
                    <input
                      class="w-16 h-12 text-center text-xl rounded border max-md:w-14"
                      readonly
                      type="text"
                      :value="cartItem.quantity"
                    />
                    <button
                      @click="updateCartItem(cartItem.quantity + 1)"
                      class="rounded-lg inline-flex justify-center items-center h-12.5 w-12.5 bg-main-color ease-linear transition-all duration-150 hover:bg-accent-color max-md:w-20 max-md:h-11"
                    >
                      <IconPlusRegular
                        svgClass="h-6 w-6 max-md:h-5 max-md:w-5"
                        pathClass="stroke-white"
                      />
                    </button>
                  </div>
                </div>
              </template>

              <div class="flex gap-3 justify-between">
                <CommentButton v-if="detail?.allow_comments"></CommentButton>
                <RateButton></RateButton>
                <ReportButton></ReportButton>
              </div>
            </div>
            <!-- 
                        <div class="mt-5 bg-white  divide-y rounded mb-5">
                            <p class="text-main-color text-base font-semibold p-4">{{ $t('page.detail') }}</p>
                            <div class="p-4 flex flex-col gap-4"> -->
            <!-- <div class="w-full flex items-center justify-between">
                                    <p class="text-main-color font-semibold text-base">{{ $t('page.productCode') }}:
                                    </p>
                                    <p class="text-main-color">{{ detail?.code }}</p>
                                </div> -->
            <!-- <div class="w-full flex items-center justify-between">
                                    <p class="text-main-color font-semibold text-base">{{ $t('page.location') }}:</p>
                                    <p class="text-main-color">{{ detail?.store?.location }}</p>
                                </div> -->

            <!-- </div>
                        </div> -->

            <div class="flex gap-1 my-5">
              <IconComment></IconComment>
              <p>{{ detail.comments__count }} {{ $t('page.comments') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col w-full gap-5 border-b border-[#E0E0E0] my-10 pb-10">
      <p class="text-2xl font-bold text-main-color mb-2 text-center">{{ $t('page.info') }}</p>
      <div>
        <p class="font-light text-base text-[#333]">{{ detail?.description }}</p>
      </div>
    </div>
    <div v-if="similarExhibitions.length > 0">
      <p class="font-semibold text-main-color text-3xl mb-5">{{ $t('page.similarProducts') }}</p>

      <div class="flex overflow-hidden gap-5">
        <FoodCard
          v-for="(product, index) in similarExhibitions"
          :key="index"
          @likeAction="likeActionFoodCard"
          :detail="product"
        />
      </div>
    </div>
    <div v-if="comments.length">
      <p class="font-bold text-main-color text-3xl mt-20 mb-5 capitalize">
        {{ $t('page.comments') }}
      </p>

      <!-- TESWIRLER -->
      <vue-easy-lightbox
        :visible="visibleAttachmentRef"
        :imgs="attachmentImgsRef"
        :index="0"
        @hide="visibleAttachmentRef = false"
      ></vue-easy-lightbox>

      <div
        class="border-b border-[#E0E0E0] pb-5 mb-3"
        v-for="(comment, index) in comments"
        :key="index"
      >
        <div class="flex flex-col mb-4">
          <div class="flex flex-col">
            <div class="flex flex-col mb-2">
              <div class="flex flex-col mb-3">
                <p class="text-lg text-main-color font-bold">{{ comment.username }}</p>
                <p class="test-[#333333] font-light text-sm">
                  {{ formatDate(comment.date_created) }}
                </p>
              </div>
              <p class="font-light text-lg text-[#333]">{{ comment.text }}</p>
            </div>

            <div class="flex gap-5 items-center overflow-x-scroll mb-2">
              <div v-for="attachment in comment.attachments" :key="attachment.id">
                <img
                  @click="showAttachment(attachment.path)"
                  :src="attachment.path"
                  alt="Attachment Image"
                  class="max-w-[156px] max-h-[90px]"
                />
              </div>
            </div>
            <div class="flex gap-4 justify-end mb-2">
              <button class="flex gap-1 text-gray-600" @click="postCommentVote(comment.id, 1)">
                <IconLike :class="{ 'fill-[#a5ffab]': comment?.user_vote == 1 }" />
                {{ comment.up_vote_count }}
              </button>
              <button class="flex gap-1 text-gray-600" @click="postCommentVote(comment.id, -1)">
                <IconDislike :class="{ 'fill-[#ffb8b8]': comment?.user_vote == -1 }" />
                {{ comment.down_vote_count }}
              </button>
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <div v-for="reply in comment.replies" :key="reply.id" class="bg-[#e3e3e3] p-4 rounded">
              <div class="flex flex-col border-l border-solid border-main-color pl-3">
                <div class="flex items-center gap-4">
                  <p class="font-bold">{{ $t('page.owner') }}</p>
                  <p class="text-sm">{{ reply.date_created }}</p>
                </div>

                <div class="mt-2">
                  <p>{{ reply.text }}</p>
                </div>

                <div
                  v-if="reply.attachments && reply.attachments.length"
                  class="flex gap-5 items-center overflow-x-scroll mt-3"
                >
                  <div
                    v-for="attachment in reply.attachments"
                    :key="`reply-attachment-${attachment.id}`"
                  >
                    <img
                      @click="showAttachment(attachment.path)"
                      :src="attachment.path"
                      alt="Reply Attachment Image"
                      class="max-w-[156px] max-h-[90px] cursor-pointer"
                    />
                  </div>
                </div>
              </div>
              <div class="flex gap-4 justify-end">
                <button class="flex gap-1 text-gray-600" @click="postCommentVote(reply.id, 1)">
                  <IconLike :class="{ 'fill-[#a5ffab]': reply?.user_vote == 1 }" />
                  {{ reply.up_vote_count }}
                </button>
                <button class="flex gap-1 text-gray-600" @click="postCommentVote(reply.id, -1)">
                  <IconDislike :class="{ 'fill-[#ffb8b8]': reply?.user_vote == -1 }" />
                  {{ reply.down_vote_count }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import { i18n } from '@/i18n';
import { mapState } from 'pinia';
import { useAppStore } from '@/store';
import VueEasyLightbox from 'vue-easy-lightbox';
import VueStarRating from 'vue-star-rating';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import { Field, Form, ErrorMessage } from 'vee-validate';
import DetailBanner from '@cmp/DetailBanner.vue';
import FoodCard from '@cmp/food/FoodCard.vue';
import IconPlus from '@icons/IconPlus.vue';
import IconPlusRegular from '@icons/IconPlusRegular.vue';
import IconMinus from '@icons/IconMinus.vue';
import IconHeart from '@icons/IconHeart.vue';
import IconEye from '@icons/IconEye.vue';
import IconClip from '@icons/IconClip.vue';
import IconComment from '@icons/IconComment.vue';
import IconChecked from '@icons/IconChecked.vue';
import IconUnchecked from '@icons/IconUnchecked.vue';
import IconTrash from '@icons/IconTrash.vue';
import { formatDate, formatNumber, isVideo } from '@/helpers';

import Modal from '@cmp/Modal.vue';
import InlineLoader from '@cmp/InlineLoader.vue';
import IconLike from '@icons/IconLike.vue';
import IconDislike from '@icons/IconDislike.vue';
import ReportModal from '@cmp/modals/ReportModal.vue';
import ReportButton from '@cmp/modals/buttons/ReportButton.vue';
import CommentButton from '@cmp/modals/buttons/CommentButton.vue';
import RateButton from '@cmp/modals/buttons/RateButton.vue';
import CommentModal from '@cmp/modals/CommentModal.vue';
import RateModal from '@cmp/modals/RateModal.vue';
export default {
  name: 'foodRestaurantProductDetail',

  components: {
    VueEasyLightbox,
    VueStarRating,
    Splide,
    SplideSlide,
    SplideTrack,
    Field,
    Form,
    ErrorMessage,
    DetailBanner,
    FoodCard,
    IconPlus,
    IconPlusRegular,
    IconMinus,
    IconHeart,
    IconEye,
    IconClip,
    IconComment,
    IconTrash,
    IconChecked,
    IconUnchecked,
    Modal,
    InlineLoader,
    IconLike,
    IconDislike,
    ReportModal,
    ReportButton,
    CommentButton,
    CommentModal,
    RateButton,
    RateModal,
  },
  data() {
    return {
      loading: false,
      currentId: null,
      detail: {},
      comments: [],
      advertisements: [],
      visibleRef: false,
      visibleAttachmentRef: false,
      indexRef: 0,
      triggerComputed: 0,
      imgsRef: [],
      attachmentImgsRef: [],
      currentImage: null,
      commentModal: false,
      rateModal: false,
      similarExhibitions: [],
      productPrice: 0,
      selectedStock: null,
      isLiked: false,
      files: [],
      fileUrls: [],
      fileIds: [],
      userRating: 0,
      formComment: {
        text: '',
        attachments: [],
      },
      formRate: {
        rating: 0,
      },
      imageOptions: {
        perPage: 3,
        perMove: 1,
        gap: '10px',
      },
    };
  },
  watch: {
    '$route.params.id': {
      async handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.currentId = newValue;
          await this.loadPageData();
        }
      },
    },
  },

  methods: {
    formatDate,
    formatNumber,
    isVideo,

    async loadPageData() {
      const detailLoaded = await this.getExhibitionDetail();

      if (!detailLoaded) {
        this.comments = [];
        this.similarExhibitions = [];
        this.currentImage = null;
        return;
      }

      await Promise.all([
        this.getExhibitionSimilars(),
        this.getExhibitionComments(),
        this.getExhibitionAd(),
      ]);
    },

    onShow() {
      this.visibleRef = true;
      this.imgsRef = this.detail.images;
    },
    onAttachmentHide() {},
    onHide() {
      this.visibleRef = false;
    },
    showAttachment(img) {
      this.visibleAttachmentRef = true;
      this.attachmentImgsRef = [img];
    },

    changeImage(index) {
      this.currentImage = this.detail.images[index].path;
    },
    selectStock(stock) {
      this.selectedStock = stock;
      this.productPrice = stock.price;
      this.triggerComputed++;
    },

    remove(i) {
      this.files.splice(i, 1);
      this.fileIds.splice(i, 1);
    },

    async onFileChange() {
      for (var x = 0; x < this.$refs.file.files.length; x++) {
        const fileForm = new FormData();
        const f = this.$refs.file.files[x];
        fileForm.append('path', f);
        this.loading = true;
        const response = await api.post('files/images/', fileForm, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        if (response && response.status === 201) {
          this.files.push({
            detail: this.$refs.file.files[x],
            path: URL.createObjectURL(this.$refs.file.files[x]),
            isUploaded: true,
          });
          this.fileIds.push(response.data.id);
        } else {
          this.$toast.warning(`${i18n.global.t('warning.UPLOAD_FAILED')}`);
        }
        this.loading = false;
      }
    },

    async likeActionFoodCard(id, like) {
      if (this.$store.authStatus === false) {
        this.$store.setState('loginModal', true);
        return;
      }
      try {
        await api.post(`exhibition/products/${id}/like/`, { is_liked: like });
        this.$store.getLiked('exhibition');
      } catch (e) {
        console.log(e);
      }
    },

    async likeAction() {
      if (this.$store.authStatus === false) {
        this.$store.setState('loginModal', true);
        return;
      }
      try {
        this.loading = true;
        await api.post(`exhibition/products/${this.currentId}/like/`, {
          is_liked: !this.isLiked,
        });
        this.isLiked = !this.isLiked;
        this.$store.getLiked('exhibition');
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },

    async getExhibitionAd() {
      try {
        const locationId =
          this.detail?.store?.advertisement_location_id ||
          this.detail?.store?.location_id ||
          this.getSelectedLocationId();

        const response = await api.get('commercials/advertisements/', {
          params: {
            type: 205,
            location: locationId,
          },
        });

        this.advertisements = response.data;
      } catch (e) {
        console.log(e);
      }
    },

    getSelectedLocationId() {
      const selectedLocation = JSON.parse(localStorage.getItem('selectedLocation')) || 1;

      if (typeof selectedLocation === 'object') {
        return selectedLocation?.id || null;
      }

      return selectedLocation;
    },

    async getExhibitionDetail() {
      try {
        const response = await api.get(`exhibition/products/${this.currentId}/`);
        this.detail = response.data || {};
        this.currentImage = this.detail?.images?.[0]?.path || this.detail?.thumbnail || null;
        this.isLiked = !!this.detail?.is_liked;
        this.formRate.rating = this.detail?.user_rating?.rating || 0;
        this.userRating = this.detail?.user_rating?.rating || 0;
        this.productPrice = this.detail?.price || 0;
        return true;
      } catch (e) {
        this.detail = {};
        this.comments = [];
        this.similarExhibitions = [];
        this.currentImage = null;
        this.isLiked = false;
        this.formRate.rating = 0;
        this.userRating = 0;
        this.productPrice = 0;
        console.log(e);
        return false;
      }
    },

    async getExhibitionComments() {
      try {
        const response = await api.get(`exhibition/products/${this.currentId}/comment/`);
        this.comments = response.data.results;
      } catch (e) {
        this.comments = [];
        console.log(e);
      }
    },

    async getExhibitionSimilars() {
      try {
        const response = await api.get(`exhibition/products/${this.currentId}/more_like_this/`);
        this.similarExhibitions = response.data.results;
      } catch (e) {
        this.similarExhibitions = [];
        console.log(e);
      }
    },

    async addToCart() {
      this.loading = true;
      try {
        let body = {
          product: this.detail.id,
          quantity: 1,
          session: this.getFoodOrderSessionId,
        };
        await api.post('exhibition/cart-items/', body);
        this.$toast.info(`${i18n.global.t('warning.CART_ADDED')}`);
        await this.$store.getSessionFoodCart();
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },

    deleteCartItem(qty) {
      this.$store.deleteFoodCartItem(this.cartItem.id, qty).then(() => {
        this.loading = false;
      });
    },

    updateCartItem(qty) {
      this.loading = true;
      if (qty === 0) {
        this.deleteCartItem(this.cartItem.id, qty);
        return;
      }
      this.$store.updateFoodCartItem(this.cartItem.id, qty).then(() => {
        this.loading = false;
      });
    },

    async postCommentVote(id, value) {
      try {
        await api.post(`comments/${id}/vote/`, { vote: value });
        await this.getExhibitionComments();
      } catch (e) {
        console.log(e);
      }
    },

    setUserRate(value) {
      this.userRating = value;
    },
  },

  computed: {
    ...mapState(useAppStore, ['getToken', 'getFoodOrderSessionId', 'getFoodCartItemById']),

    restaurantFirstPhone() {
      const phones = this.detail?.store?.phone_numbers;

      if (Array.isArray(phones) && phones.length > 0) {
        return phones[0];
      }

      return '';
    },

    cartItem() {
      this.triggerComputed;

      return this.getFoodCartItemById(this.detail?.id);
    },
  },
  async created() {
    this.currentId = this.$route.params.id;
    await this.loadPageData();
  },
};
</script>
